import React, { useMemo, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { GoOrganization } from "react-icons/go";
import {
  Chip, ChipScroll, Group, SearchField, Empty, Section, LoadMore,
} from "../../activity-logs/styles";
import { SegmentedControl } from "../../activity-logs/components/primitives";
import { DirectoryCard, StepNumber, SupplierGrid, Tag, TaxBar } from "../styles";
import { MoneyValue, SupplierAvatar } from "./parts";
import { ABC } from "../supplierStats";
import { TAXES, displayName, formatCNPJ, formatCount, formatCurrency, formatPercent } from "../dashboardData";

const PAGE_SIZE = 36;

// Card do diretório: quem é, quanto vale e como o valor se divide entre os impostos
function SupplierCard({ s, delay, onOpen }) {
  const taxes = TAXES.filter((t) => s.byTax[t.key]);
  return (
    <DirectoryCard type="button" $delay={delay} onClick={() => onOpen(s)}>
      <span className="head">
        <SupplierAvatar name={s.name} size={40} />
        <span className="who">
          <span className="name" title={s.name}>{displayName(s.name)}</span>
          <span className="id">{formatCNPJ(s.cnpj) || s.uf || "Sem CNPJ"}</span>
        </span>
        {s.abc && (
          <StepNumber $color={ABC[s.abc].color} title={ABC[s.abc].label} style={{ width: 20, height: 20, fontSize: "0.64em" }}>
            {s.abc}
          </StepNumber>
        )}
      </span>
      <span className="body">
        <span className="amount"><MoneyValue value={s.total} /></span>
        <span className="meta">
          {formatCount(s.count)} {s.count === 1 ? "item" : "itens"}
          {s.share !== undefined ? ` · ${formatPercent(s.share)} do crédito` : ""}
        </span>
        <TaxBar className="bar">
          {taxes.map((t) => (
            <span key={t.key} title={`${t.label}: ${formatCurrency(s.byTax[t.key])}`} style={{ flexGrow: s.byTax[t.key], background: t.color }} />
          ))}
        </TaxBar>
        <span className="taxes">
          {taxes.map((t) => <Tag key={t.key} $color={t.color}>{t.label}</Tag>)}
        </span>
      </span>
    </DirectoryCard>
  );
}

// Diretório completo da aba: busca, classe ABC e ordenação
export default function SupplierList({ suppliers, classes = [], abc = "", setAbc, onOpen }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("value");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const digits = term.replace(/\D/g, "");
    const list = suppliers.filter((s) => (!abc || s.abc === abc) && (!term
      || s.name.toLowerCase().includes(term)
      || (digits && String(s.cnpj).includes(digits))));
    if (sort === "count") return [...list].sort((a, b) => b.count - a.count);
    if (sort === "perItem") return [...list].sort((a, b) => b.perItem - a.perItem);
    if (sort === "az") return [...list].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    return list;
  }, [suppliers, q, sort, abc]);

  const shown = filtered.slice(0, visible);
  const chooseClass = (key) => { setAbc(key); setVisible(PAGE_SIZE); };

  return (
    <Section>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <SearchField $max="420px">
          <FiSearch />
          <input placeholder="Buscar fornecedor ou CNPJ" aria-label="Buscar fornecedor" value={q} onChange={(e) => { setQ(e.target.value); setVisible(PAGE_SIZE); }} />
          {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
        </SearchField>
        <div style={{ flex: 1 }} />
        <SegmentedControl
          small
          value={sort}
          onChange={setSort}
          options={[{ key: "value", label: "Maior valor" }, { key: "perItem", label: "Valor por item" }, { key: "count", label: "Mais itens" }, { key: "az", label: "A–Z" }]}
        />
      </div>

      {classes.length > 0 && (
        <ChipScroll>
          <Chip type="button" $active={!abc} onClick={() => chooseClass("")}>Todas · {formatCount(suppliers.length)}</Chip>
          {classes.map((c) => (
            <Chip key={c.key} type="button" $active={abc === c.key} $color={c.color} onClick={() => chooseClass(abc === c.key ? "" : c.key)}>
              {c.label} · {formatCount(c.count)}
            </Chip>
          ))}
        </ChipScroll>
      )}

      {filtered.length === 0 ? (
        <div style={{ marginTop: 10 }}>
          <Group>
            <Empty>
              <div className="e-icon"><GoOrganization /></div>
              <div className="e-title">{q ? "Nenhum resultado" : "Nenhum fornecedor identificado"}</div>
              <div className="e-sub">{q ? `Nada encontrado para "${q}".` : "Os créditos deste recorte não têm fornecedor informado."}</div>
            </Empty>
          </Group>
        </div>
      ) : (
        <SupplierGrid>
          {shown.map((s, idx) => (
            <SupplierCard key={s.id} s={s} delay={Math.min(idx, 11) * 25} onOpen={onOpen} />
          ))}
        </SupplierGrid>
      )}

      {visible < filtered.length && (
        <LoadMore type="button" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
          Mostrar mais ({formatCount(filtered.length - visible)} restantes)
        </LoadMore>
      )}
    </Section>
  );
}
